import { useState, useEffect, useRef } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { PageHeader } from '@/components/layout/PageHeader';
import { ColorSettings } from '@/components/settings/whitelabel/ColorSettings';
import { ImageSettings } from '@/components/settings/whitelabel/ImageSettings';
import { TypographySettings } from '@/components/settings/whitelabel/TypographySettings';
import { BrandingPreview } from '@/components/settings/whitelabel/BrandingPreview';
import {
  pickOwnColors,
  pickInheritedColors,
  buildColorsPayload,
  buildFontPayload,
} from '@/components/settings/whitelabel/brandingDraft';
import { Button } from '@/components/ui/Button';
import { Icon } from '@/components/ui/Icon';
import { LockCircle } from '@/components/gating/LockCircle';
import { ConfirmationModal } from '@/components/ui/ConfirmationModal';
import { ICONS } from '@/constants/icons';
import { useAuth } from '@/hooks/useAuth';
import { useTheme } from '@/hooks/useTheme';
import { useUnsavedGuard } from '@/hooks/useUnsavedGuard';
import { cn } from '@/utils/cn';
import {
  getAgencyBranding,
  updateAgencyBranding,
  resetBranding,
} from '@/services/branding.service';
import {
  BrandingInput,
  BrandingColors,
  BrandingAssets,
  BrandingFont,
  TenantType,
} from '@/types/branding.types';
import { toast } from '@/utils/toast';
import { devLog } from '@/utils/logger';

/**
 * AgencyWhitelabelSettings Page
 *
 * Whitelabel customization interface for agencies.
 * Mirrors the WhitelabelSettings page pattern but scoped to agencyId.
 *
 * Features:
 * - Color customization
 * - Image/asset management
 * - Typography selection
 * - Live preview
 * - Save functionality
 */

type SettingsTab = 'colors' | 'images' | 'typography' | 'preview';

export const AgencyWhitelabelSettings = () => {
  const { t } = useTranslation(['settings', 'common']);
  const { user } = useAuth();
  const { refreshBranding } = useTheme();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<SettingsTab>('colors');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [resetConfirmOpen, setResetConfirmOpen] = useState(false);

  useUnsavedGuard(hasUnsavedChanges);

  // Local state for draft changes
  const [draftColors, setDraftColors] = useState<BrandingColors>({});
  const [draftAssets, setDraftAssets] = useState<BrandingAssets>({});
  const [draftFont, setDraftFont] = useState<BrandingFont | undefined>();
  const [draftHeadlineFont, setDraftHeadlineFont] = useState<BrandingFont | undefined>();

  // Get agency ID from user context
  const agencyId = user?.agencyId;
  const canManageWhitelabel = user?.agencyRole === 'OWNER' || user?.agencyRole === 'ADMIN' || user?.agencyRole === 'MANAGER';
  // /me resolves this from the agency Plan OR an active Atelier Mode add-on.
  const hasWhitelabelAccess = user?.agency?.whitelabelEnabled === true;

  // Fetch current branding
  const {
    data: branding,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['branding', 'agency', agencyId],
    queryFn: () => getAgencyBranding(agencyId!),
    enabled: !!agencyId && canManageWhitelabel && hasWhitelabelAccess,
  });

  // Initialize draft state when branding first loads.
  // Guarded with a ref so background refetches (e.g. window-focus refetch,
  // or invalidation from handleAssetsChange) never re-seed the draft and
  // clobber in-progress unsaved edits. Post-save/reset state is refreshed
  // explicitly by the mutations' onSuccess handlers below.
  const seededRef = useRef(false);
  useEffect(() => {
    if (branding && !seededRef.current) {
      setDraftColors(pickOwnColors(branding, 'agency'));
      setDraftAssets(branding.assets || {});
      setDraftFont(branding.font);
      setDraftHeadlineFont(branding.headlineFont);
      seededRef.current = true;
    }
  }, [branding]);

  // Save branding mutation
  const saveMutation = useMutation({
    mutationFn: (input: BrandingInput) => updateAgencyBranding(agencyId!, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['branding', 'agency', agencyId] });
      setHasUnsavedChanges(false);
      toast.success(t('settings_whitelabel_success_save'));
      // Allow the ensuing refetch to re-seed the draft from the authoritative
      // saved branding, so fields stripped from the payload (e.g. emptied
      // hex colors) reflect the merged server value instead of the stale
      // local draft. Safe here: hasUnsavedChanges is false, so there is no
      // in-progress edit to clobber.
      seededRef.current = false;
      // Refresh live theme so changes are visible immediately without page reload
      refreshBranding();
    },
    onError: (error) => {
      devLog.error('Save error:', error);
      toast.error(t('settings_whitelabel_error_save'));
    },
  });

  // Reset branding mutation
  const resetMutation = useMutation({
    mutationFn: () => resetBranding(TenantType.AGENCY, agencyId!),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['branding', 'agency', agencyId] });
      setDraftColors(pickOwnColors(data, 'agency'));
      setDraftAssets(data.assets || {});
      setDraftFont(data.font);
      setDraftHeadlineFont(data.headlineFont);
      setHasUnsavedChanges(false);
      setResetConfirmOpen(false);
      toast.success(t('settings_whitelabel_success_reset'));
      refreshBranding();
    },
    onError: (error) => {
      devLog.error('Reset error:', error);
      toast.error(t('settings_whitelabel_error_reset'));
    },
  });

  // Reset handler
  const handleReset = () => {
    setResetConfirmOpen(true);
  };

  // Handle changes
  const handleColorsChange = (colors: BrandingColors) => {
    setDraftColors(colors);
    setHasUnsavedChanges(true);
  };

  const handleAssetsChange = (assets: BrandingAssets) => {
    setDraftAssets(assets);
    // Don't set hasUnsavedChanges — images are uploaded/deleted directly to S3 and DB
    // Refresh theme + re-fetch branding query so assetSources updates (badge accuracy)
    refreshBranding();
    queryClient.invalidateQueries({ queryKey: ['branding', 'agency', agencyId] });
  };

  const handleFontChange = (font: BrandingFont | undefined) => {
    setDraftFont(font);
    setHasUnsavedChanges(true);
  };

  const handleHeadlineFontChange = (font: BrandingFont | undefined) => {
    setDraftHeadlineFont(font);
    setHasUnsavedChanges(true);
  };

  // Save handler
  const handleSave = () => {
    // Empty color fields: a saved own value (source 'agency') is cleared with null; empty unsaved
    // fields are omitted (empty strings fail backend validation).
    const cleanedColors = buildColorsPayload(draftColors, branding, 'agency');

    // Don't include assets — images are managed independently via upload/delete endpoints
    // Including them here would persist ALL resolved assets (including defaults) as agency overrides
    const input: BrandingInput = {
      colors: cleanedColors,
      font: buildFontPayload(draftFont, branding?.font, branding?.sources?.font, 'agency'),
      headlineFont: buildFontPayload(
        draftHeadlineFont,
        branding?.headlineFont,
        branding?.sources?.headlineFont,
        'agency'
      ),
    };

    saveMutation.mutate(input);
  };

  // PrivateRoute resolves auth before rendering this page. Match the parent Whitelabel tab's role rule here too.
  if (user && !canManageWhitelabel) {
    return <Navigate to="/agency/settings?tab=general" replace />;
  }

  // No agency ID guard
  if (!agencyId) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center max-w-md px-6">
          <Icon name={ICONS.ERROR} size="xl" color="text-red-500" className="mx-auto mb-4" ariaHidden />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('settings_whitelabel_no_agency_title', { defaultValue: 'No Brand Portfolio Found' })}</h2>
          <p className="text-gray-600 mb-4">
            {t('settings_whitelabel_no_agency_desc', { defaultValue: 'Could not find a brand portfolio associated with your account.' })}
          </p>
          <Button onClick={() => navigate('/agency/settings')}>{t('settings_whitelabel_back_button', { defaultValue: 'Back to Brand Portfolio Settings' })}</Button>
        </div>
      </div>
    );
  }

  // A Brand Portfolio can exist without Atelier Mode. Match the team page's
  // locked state and keep the editing controls out of reach until entitled.
  if (!hasWhitelabelAccess) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="max-w-2xl px-6 text-center">
          <LockCircle size={80} glyphSize={36} className="mx-auto mb-6 bg-gradient-to-br from-secondary-100 to-secondary-200 text-secondary-600" />
          <h2 className="mb-3 text-3xl font-bold text-gray-900">{t('settings_whitelabel_upgrade_title')}</h2>
          <p className="mb-8 text-lg text-gray-600">{t('settings_whitelabel_upgrade_subtitle')}</p>
          <div className="mb-8 rounded-lg bg-gray-50 p-6 text-left">
            <h3 className="mb-4 text-sm font-semibold uppercase tracking-wide text-gray-900">{t('settings_whitelabel_features_label')}</h3>
            <ul className="space-y-3">
              {(['colors', 'images', 'fonts', 'client'] as const).map((feature) => (
                <li key={feature} className="flex items-start gap-3">
                  <Icon name={ICONS.CHECK} size="sm" color="text-green-600" className="mt-0.5 flex-shrink-0" ariaHidden />
                  <span className="text-sm text-gray-700">{t(`settings_whitelabel_feature_${feature}`)}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <Button variant="secondary" size="lg" onClick={() => navigate('/agency/settings?tab=general')}>
              <Icon name={ICONS.ARROW_LEFT} size="xs" className="mr-2" ariaHidden />
              {t('settings_whitelabel_back_button')}
            </Button>
            <Button variant="primary" size="lg" onClick={() => navigate('/shop?details=atelier-mode#shop-category-add-ons')}>
              <Icon name={ICONS.CROWN} size="xs" className="mr-2" ariaHidden />
              {t('settings_whitelabel_shop_button')}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <Icon name={ICONS.LOADER} size="xl" className="animate-spin mx-auto mb-4 text-secondary-500" ariaHidden />
          <p className="text-gray-600">{t('settings_whitelabel_loading')}</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center max-w-md">
          <Icon name={ICONS.ERROR} size="xl" color="text-red-500" className="mx-auto mb-4" ariaHidden />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('settings_whitelabel_error_title')}</h2>
          <p className="text-gray-600 mb-4">
            {t('settings_whitelabel_error_desc')}
          </p>
          <Button onClick={() => window.location.reload()}>{t('settings_whitelabel_reload_button')}</Button>
        </div>
      </div>
    );
  }

  const tabs: { id: SettingsTab; label: string; icon: string }[] = [
    { id: 'colors', label: t('settings_whitelabel_tab_colors'), icon: ICONS.COLOR },
    { id: 'images', label: t('settings_whitelabel_tab_images'), icon: ICONS.IMAGE },
    { id: 'typography', label: t('settings_whitelabel_tab_typography'), icon: ICONS.PEN },
    { id: 'preview', label: t('settings_whitelabel_tab_preview'), icon: ICONS.EYE },
  ];

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <PageHeader
        title={t('settings_agency_whitelabel_page_title', { defaultValue: 'Atelier Mode' })}
        subtitle={t('settings_whitelabel_page_subtitle')}
        icon={ICONS.PAINT}
        headerType="dashboard"
        documentTitle="Atelier Mode"
        backTo="/agency/settings?tab=whitelabel"
        backLabel={t('agency_settings_back_label', { defaultValue: 'Brand Portfolio Settings' })}
      />

      {/* Tabs + Action Buttons Row */}
      <div className="sticky top-[4.1rem] z-30 bg-gray-50/95 backdrop-blur-sm py-3 sm:py-2 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-4">
        {/* Pill-Style Tabs — mobile: 4-col compact grid, desktop: inline row */}
        <div className="bg-gray-100/80 backdrop-blur-sm p-2 rounded-2xl">
          {/* Mobile grid */}
          <div className="grid grid-cols-4 gap-1 sm:hidden">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={cn(
                  'flex flex-col items-center justify-center gap-1 px-2 py-2.5 rounded-xl transition-all duration-200',
                  activeTab === tab.id
                    ? 'bg-white shadow-sm text-gray-900'
                    : 'text-gray-500 hover:text-gray-900 hover:bg-white/50'
                )}
                aria-current={activeTab === tab.id ? 'page' : undefined}
              >
                <Icon
                  name={tab.icon}
                  size="sm"
                  color={activeTab === tab.id ? 'text-primary-600' : 'text-gray-400'}
                  ariaHidden
                />
                <span className="text-[10px] font-medium leading-tight truncate max-w-full">{tab.label}</span>
              </button>
            ))}
          </div>
          {/* Desktop inline row */}
          <div className="hidden sm:inline-flex gap-1">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={cn(
                  'group inline-flex items-center gap-2 px-5 py-2.5 rounded-xl font-medium text-sm transition-all duration-200 whitespace-nowrap',
                  activeTab === tab.id
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-white/50'
                )}
                aria-current={activeTab === tab.id ? 'page' : undefined}
              >
                <Icon
                  name={tab.icon}
                  size="sm"
                  color={activeTab === tab.id ? 'text-primary-600' : 'text-gray-400 group-hover:text-gray-600'}
                  ariaHidden
                />
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Design reference (always visible) + Save/Reset (only when changes exist) */}
        <div className="flex items-center gap-3 w-full sm:w-auto justify-end">
          <Link
            to="/design-system"
            className="hidden sm:inline-flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600 transition-colors"
          >
            <Icon name={ICONS.EXTERNAL_LINK} size="xs" ariaHidden />
            {t('settings_atelier_design_reference', { defaultValue: 'Design reference' })}
          </Link>

          {hasUnsavedChanges && (
            <div className="flex items-center gap-2 animate-fade-in w-full sm:w-auto">
              <Button
                variant="secondary"
                size="sm"
                onClick={handleReset}
                disabled={resetMutation.isPending}
                className="flex-1 sm:flex-none flex items-center justify-center gap-1.5"
              >
                <Icon name={ICONS.REFRESH} size="sm" ariaHidden />
                {t('settings_whitelabel_reset_button')}
              </Button>

              <Button
                variant="primary"
                size="sm"
                onClick={handleSave}
                disabled={!hasUnsavedChanges || saveMutation.isPending}
                loading={saveMutation.isPending}
                className="flex-1 sm:flex-none flex items-center justify-center gap-1.5"
              >
                <Icon name={ICONS.SAVE} size="sm" ariaHidden />
                {t('settings_whitelabel_save_button')}
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Tab Content */}
      <div className="relative overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
        <div className="absolute inset-x-0 top-0 h-1 bg-gradient-mint" />
        <div className="p-4 sm:p-6">
          {activeTab === 'colors' && (
            <ColorSettings
              colors={draftColors}
              onChange={handleColorsChange}
              inheritedColors={branding ? pickInheritedColors(branding, 'agency') : undefined}
              scope="standalone"
            />
          )}

          {activeTab === 'images' && (
            <ImageSettings
              tenantType={TenantType.AGENCY}
              tenantId={agencyId}
              assets={draftAssets}
              assetSources={branding?.sources?.assets}
              onChange={handleAssetsChange}
            />
          )}

          {activeTab === 'typography' && (
            <TypographySettings
              font={draftFont}
              headlineFont={draftHeadlineFont}
              onChange={handleFontChange}
              onHeadlineFontChange={handleHeadlineFontChange}
              inheritedFont={branding?.sources?.font !== 'agency' ? branding?.font : undefined}
              inheritedHeadlineFont={
                branding?.sources?.headlineFont !== 'agency' ? branding?.headlineFont : undefined
              }
              scope="standalone"
            />
          )}

          {activeTab === 'preview' && (
            <BrandingPreview
              branding={{
                colors: {
                  ...(branding?.colors ?? {}),
                  ...Object.fromEntries(
                    Object.entries(draftColors).filter(([, v]) => typeof v === 'string' && v.trim())
                  ),
                },
                assets: draftAssets,
                font: draftFont,
                headlineFont: draftHeadlineFont,
              }}
            />
          )}
        </div>
      </div>

      {/* Reset Confirmation Modal */}
      <ConfirmationModal
        open={resetConfirmOpen}
        onOpenChange={setResetConfirmOpen}
        title={t('settings_whitelabel_confirm_reset_title', { defaultValue: 'Reset Branding' })}
        description={t('settings_whitelabel_confirm_reset', { defaultValue: 'This will reset all colors, fonts, and settings to defaults. Uploaded images will not be affected. This action cannot be undone.' })}
        confirmText={t('settings_whitelabel_reset_button')}
        variant="warning"
        onConfirm={() => resetMutation.mutate()}
        isLoading={resetMutation.isPending}
      />
    </div>
  );
};
